/** Common words stripped when tokenizing design names for fuzzy matching. */
const STOP_WORDS = new Set([
  't', 'shirt', 'tshirt', 'tee', 'tees', 'the', 'a', 'an', 'and', 'for', 'of'
]);

/**
 * Normalize a design name for comparison and index keys.
 * @param {string} name
 * @returns {string}
 */
export function normalizeDesignName(name) {
  if (!name || typeof name !== 'string') return '';
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Meaningful tokens from a design name (stop words removed).
 * @param {string} name
 * @returns {string[]}
 */
export function getDesignTokens(name) {
  return normalizeDesignName(name)
    .split(' ')
    .filter((token) => token.length > 1 && !STOP_WORDS.has(token));
}

/**
 * Score how similar two design names are (0–1).
 * Uses Jaccard similarity on tokens plus a substring bonus.
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
export function scoreDesignNameSimilarity(a, b) {
  if (!a || !b) return 0;

  const normA = normalizeDesignName(a);
  const normB = normalizeDesignName(b);
  if (!normA || !normB) return 0;
  if (normA === normB) return 1;

  const tokensA = new Set(getDesignTokens(a));
  const tokensB = new Set(getDesignTokens(b));
  if (tokensA.size === 0 || tokensB.size === 0) return 0;

  let intersection = 0;
  for (const token of tokensA) {
    if (tokensB.has(token)) intersection += 1;
  }

  const union = new Set([...tokensA, ...tokensB]).size;
  const jaccard = intersection / union;

  let substringBonus = 0;
  if (normA.includes(normB) || normB.includes(normA)) {
    substringBonus = 0.15;
  }

  return Math.min(1, jaccard + substringBonus);
}

/**
 * Firestore document id for a customer + design name pair.
 * @param {string} customerId
 * @param {string} designName
 * @returns {string|null}
 */
export function buildDesignIndexDocId(customerId, designName) {
  const normalized = normalizeDesignName(designName);
  if (!customerId || !normalized) return null;

  const slug = normalized.replace(/\s+/g, '_').slice(0, 120);
  return `${customerId}__${slug}`;
}
